import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { AssetCard } from "@/components/DashboardCards";
import { AssetAlertDialog } from "@/components/asset/AssetActionDialogs";
import { useAuth } from "@/context/AuthContext";

export default function WatchlistTab({ watchlist, marketData, priceTicks, onToggleWatchlist, navigate }) {
  const { authHeaders } = useAuth();
  const [alertAsset, setAlertAsset] = useState(null);

  const createAlert = async (condition, targetPrice, repeat = false) => {
    if (!targetPrice) {
      toast.error("Please enter a target price");
      return false;
    }
    try {
      await axios.post(`${API}/alerts/create`, {
        symbol: alertAsset.symbol,
        condition,
        target_price: parseFloat(targetPrice),
        repeat,
      }, { headers: authHeaders() });
      toast.success(`Price alert set for ${alertAsset.symbol}!`);
      return true;
    } catch (error) {
      console.error("Error creating alert:", error);
      if (error.response?.status === 401) toast.error("Please sign in to create alerts");
      else toast.error("Failed to create alert");
      return false;
    }
  };

  if (watchlist.length === 0) {
    return (
      <div data-testid="watchlist-empty" className="text-center py-12 text-gray-500">
        <Star className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p>Your watchlist is empty</p>
        <p className="text-sm mt-2">Click the star icon on any asset to add it</p>
      </div>
    );
  }

  const watched = [...(marketData.stocks || []), ...(marketData.cryptos || [])].filter(
    (asset) => watchlist.includes(asset.symbol)
  );

  return (
    <>
      <div data-testid="watchlist-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {watched.map((asset, index) => (
          <motion.div
            key={asset.symbol}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <AssetCard
              asset={asset}
              isWatched
              tick={priceTicks[asset.symbol]}
              onToggleWatchlist={onToggleWatchlist}
              onNavigate={navigate}
              onSetAlert={setAlertAsset}
            />
          </motion.div>
        ))}
      </div>

      <AssetAlertDialog
        key={alertAsset?.symbol || "none"}
        hideTrigger
        open={!!alertAsset}
        onOpenChange={(open) => !open && setAlertAsset(null)}
        defaultPrice={alertAsset?.price ? String(alertAsset.price) : ""}
        onCreate={createAlert}
      />
    </>
  );
}
